import React, { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { api } from "../lib/api";
import { getStripe } from "../lib/stripe";
import { ensureStripeCustomer } from "../lib/stripeCustomer";

const ConfirmForm = ({ onDone, onCancel, submitLabel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message || "Payment failed");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      onDone(paymentIntent);
      return;
    }

    setError(`Unexpected status: ${paymentIntent?.status}`);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <div style={{ color: "#c0392b", fontSize: 13, fontFamily: "var(--fw-mono)" }}>{error}</div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={!stripe || submitting}>
          {submitting ? "Processing…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

// Drives the Stripe-recommended subscription flow:
//   1. POST /create-subscription { customerId, priceId, userId }
//      → backend creates sub with payment_behavior=default_incomplete and
//        returns { subscriptionId, clientSecret, status }.
//   2. Mount <Elements clientSecret> and let PaymentElement collect the card.
//   3. stripe.confirmPayment() confirms the first invoice's PaymentIntent;
//      save_default_payment_method=on_subscription saves the card for renewals.
const StartSubscriptionModal = ({ project, owner, priceId, onClose, onStarted }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const customerId = await ensureStripeCustomer({
          user: { uid: project.ownerUid, email: owner?.email },
          profile: owner,
        });
        const { data } = await api.post("/create-subscription", {
          customerId,
          priceId,
          userId: project.ownerUid,
        });
        if (!live) return;
        const secret = data.clientSecret || data.client_secret;
        if (!secret) {
          setError("Backend did not return a clientSecret. Check that /create-subscription uses payment_behavior=default_incomplete and expands latest_invoice.confirmation_secret.");
          return;
        }
        setClientSecret(secret);
        setSubscriptionId(data.subscriptionId || data.id);
      } catch (err) {
        if (live) setError(err?.response?.data?.error || err.message || "Failed to start subscription");
      }
    })();
    return () => { live = false; };
  }, [project, owner, priceId]);

  const stripePromise = getStripe();

  return (
    <div className="fw-modal__backdrop" onClick={onClose}>
      <div className="fw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fw-modal__head">
          <div>
            <div className="fw-modal__eyebrow">{project.title}</div>
            <h3 className="fw-modal__title">Start subscription</h3>
          </div>
          <div className="fw-modal__amount">
            {project.paymentPlan === "lease" ? "12 mo" : "24 mo"}
          </div>
        </div>

        {error && <div className="fw-empty" style={{ color: "#c0392b" }}>{error}</div>}

        {!error && !clientSecret && <div className="fw-empty">Creating subscription…</div>}

        {clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
            <ConfirmForm
              submitLabel="Start subscription"
              onCancel={onClose}
              onDone={(pi) => { onStarted?.({ subscriptionId, paymentIntent: pi }); onClose(); }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default StartSubscriptionModal;
